import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Chip, Tooltip, IconButton, Snackbar } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getSeriesMetadata } from "../api";

const TAG_NAMES = {
  "00020010": "Transfer Syntax UID",
  "00080005": "Specific Character Set", "00080008": "Image Type", "00080016": "SOP Class UID", "00080018": "SOP Instance UID",
  "00080020": "Study Date", "00080021": "Series Date", "00080022": "Acquisition Date", "00080030": "Study Time", "00080031": "Series Time", "00080032": "Acquisition Time",
  "00080050": "Accession Number", "00080060": "Modality", "00080070": "Manufacturer", "00080080": "Institution Name", "00081030": "Study Description", "0008103E": "Series Description",
  "00100010": "Patient Name", "00100020": "Patient ID", "00100030": "Patient Birth Date", "00100040": "Patient Sex",
  "00180050": "Slice Thickness", "00185101": "View Position", "0020000D": "Study Instance UID", "0020000E": "Series Instance UID", "00200011": "Series Number", "00200013": "Instance Number", "00200037": "Image Orientation", "00200032": "Image Position", "00201041": "Slice Location",
  "00280010": "Rows", "00280011": "Columns", "00280030": "Pixel Spacing", "00281050": "Window Center", "00281051": "Window Width",
};

const formatTag = (tag = "") => tag.length === 8 ? `(${tag.slice(0, 4)},${tag.slice(4)})` : tag;

const formatValue = (entry) => {
  if (!entry) return "";
  const value = entry.Value;
  if (value == null) return entry.InlineBinary ? "[binary]" : "";
  const arr = Array.isArray(value) ? value : [value];
  return arr.map(v => {
    if (v && typeof v === "object") return v.Alphabetic || v.Ideographic || v.Phonetic || JSON.stringify(v);
    return String(v);
  }).join(" ");
};

const rowsFromMetadata = (obj = {}) => Object.entries(obj)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([tag, entry]) => ({
    tag,
    displayTag: formatTag(tag),
    name: entry?.vr ? `${TAG_NAMES[tag] || "DICOM Tag"} (${entry.vr})` : (TAG_NAMES[tag] || "DICOM Tag"),
    value: formatValue(entry),
  }));

const DicomTagsDialog = ({ open, onClose, series, studyInfo }) => {
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState([]);
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !series) return;
    setFilter("");
    setMetadata([]);
    setLoading(true);
    getSeriesMetadata(series.studyInstanceUID, series.seriesInstanceUID)
      .then(data => setMetadata(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, [open, series]);

  const rows = useMemo(() => {
    const synthetic = {
      "00100010": { vr: "PN", Value: [studyInfo?.patientName || ""] },
      "00100020": { vr: "LO", Value: [studyInfo?.patientId || ""] },
      "00080050": { vr: "SH", Value: [studyInfo?.accessionNumber || ""] },
      "00081030": { vr: "LO", Value: [studyInfo?.studyDescription || ""] },
      "00080020": { vr: "DA", Value: [studyInfo?.studyDate || ""] },
      "00080060": { vr: "CS", Value: [series?.modality || ""] },
      "00200011": { vr: "IS", Value: [series?.seriesNumber || ""] },
      "0008103E": { vr: "LO", Value: [series?.seriesDescription || ""] },
      "0020000D": { vr: "UI", Value: [series?.studyInstanceUID || ""] },
      "0020000E": { vr: "UI", Value: [series?.seriesInstanceUID || ""] },
    };
    const firstInstance = metadata[0] || {};
    const merged = { ...synthetic, ...firstInstance };
    const q = filter.trim().toLowerCase();
    return rowsFromMetadata(merged).filter(r => !q || `${r.tag} ${r.displayTag} ${r.name} ${r.value}`.toLowerCase().includes(q));
  }, [metadata, filter, series, studyInfo]);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      console.error("Clipboard copy failed", e);
    }
  };
  const rowText = (r) => `${r.displayTag}\t${r.name}\t${r.value || ""}`;
  const copyAllVisible = () => copyText(rows.map(rowText).join("\n"));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: "#101010", color: "#ddd", border: "1px solid #263238", userSelect: "text" } }}>
      <DialogTitle sx={{ color: "#00bcd4", fontSize: 14, letterSpacing: 1, userSelect: "text" }}>
        DICOM TAGS / FIELDS
        {series && <Typography component="span" sx={{ ml: 1, color: "#777", fontSize: 11 }}>{series.modality} · {series.seriesDescription}</Typography>}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "#222", userSelect: "text" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <TextField autoFocus size="small" fullWidth placeholder="Filter by tag, name, or value" value={filter} onChange={e => setFilter(e.target.value)} sx={{ input: { color: "#ddd", fontSize: 12 }, "& fieldset": { borderColor: "#333" } }} />
          <Chip size="small" label={metadata.length ? `First instance of ${metadata.length}` : "study/series tags"} sx={{ color: "#00bcd4", borderColor: "#00bcd4" }} variant="outlined" />
          <Button size="small" onClick={copyAllVisible} sx={{ color: "#00bcd4", borderColor: "#244", whiteSpace: "nowrap" }} variant="outlined">Copy visible</Button>
        </Box>
        {loading ? <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={18} /></Box> : (
          <Box sx={{ maxHeight: "62vh", overflow: "auto", border: "1px solid #222", userSelect: "text" }}>
            {rows.map(r => (
              <Box key={r.tag} sx={{ display: "grid", gridTemplateColumns: "112px 250px 1fr 32px", gap: 1, alignItems: "center", px: 1, py: 0.6, borderBottom: "1px solid #1c1c1c", fontFamily: "monospace", fontSize: 11, userSelect: "text" }}>
                <Box sx={{ color: "#00bcd4", userSelect: "text" }}>{r.displayTag}</Box>
                <Box sx={{ color: "#aaa", userSelect: "text" }}>{r.name}</Box>
                <Box sx={{ color: "#ddd", wordBreak: "break-word", userSelect: "text" }}>{r.value || "—"}</Box>
                <Tooltip title="Copy this value">
                  <IconButton size="small" onClick={() => copyText(r.value || "")} sx={{ color: "#456", p: 0.25, "&:hover": { color: "#00bcd4" } }}>
                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
        <Typography sx={{ mt: 1, color: "#666", fontSize: 10, userSelect: "text" }}>Shows study/series fields plus the first instance metadata returned by DICOMweb /metadata to avoid loading every object into the browser. Transfer Syntax UID {formatTag("00020010")} is shown when DICOMweb metadata includes it.</Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ color: "#00bcd4" }}>Close</Button></DialogActions>
      <Snackbar open={copied} autoHideDuration={1300} onClose={() => setCopied(false)} message="Copied" />
    </Dialog>
  );
};

export default DicomTagsDialog;
